import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
  Button,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  useTheme,
} from "@mui/material";
import { tokens } from "../theme";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import PeopleIcon from "@mui/icons-material/People";
import FileService from "../services/doctorServices/FileService";

/**
 * SharedFilesSelector Component
 * Admin doktorlar kendi file'larını seçip normal doktorlara paylaşabilir
 * Her file için ayrı ayrı izinler (file, forms, patients, formulas) ayarlanabilir
 */
export default function SharedFilesSelector({ sharedFiles = [], onChange }) {
  const theme = useTheme();
  const colors = useMemo(() => tokens(theme.palette.mode), [theme.palette.mode]);
  const { t } = useTranslation('permissionGroups');
  const fileService = useMemo(() => new FileService(), []);

  const [availableFiles, setAvailableFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFile, setExpandedFile] = useState(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const files = await fileService.getAllFiles();
      setAvailableFiles(files || []);
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFile = (file) => {
    const newSharedFile = {
      fileId: file.id,
      fileName: file.name, // For display purposes only
      can_view: true,
      can_update: false,
      can_delete: false,
      forms: {
        can_view: true,
        can_update: false,
        can_delete: false,
        can_add: false,
        can_send: false, // Invitation gönderme yetkisi
        can_fill: false, // Form doldurma (form answer oluşturma) yetkisi
      },
      patients: {
        can_view: true,
        can_update: false,
        can_delete: false,
        can_add: false,
      },
      formulas: {
        can_view: true,
        can_update: true,
        can_delete: true,
        can_add: true,
      },
    };

    onChange([...sharedFiles, newSharedFile]);
  };

  const handleRemoveFile = (fileId) => {
    onChange(sharedFiles.filter(sf => sf.fileId !== fileId));
  };

  const handleFilePermissionChange = (fileId, permission, value) => {
    const updated = sharedFiles.map(sf => {
      if (sf.fileId === fileId) {
        return { ...sf, [permission]: value };
      }
      return sf;
    });
    onChange(updated);
  };

  const handleSubPermissionChange = (fileId, category, permission, value) => {
    const updated = sharedFiles.map(sf => {
      if (sf.fileId === fileId) {
        return {
          ...sf,
          [category]: {
            ...sf[category],
            [permission]: value,
          },
        };
      }
      return sf;
    });
    onChange(updated);
  };

  const isFileSelected = (fileId) => {
    return sharedFiles.some(sf => sf.fileId === fileId);
  };

  const getSelectedFile = (fileId) => {
    return sharedFiles.find(sf => sf.fileId === fileId);
  };

  const getFileById = (fileId) => {
    return availableFiles.find(f => f.id === fileId);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress size={30} sx={{ color: colors.greenAccent[500] }} />
        <Typography variant="body2" sx={{ color: colors.grey[400], mt: 2 }}>
          {t('loadingFiles')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ color: colors.grey[100], mb: 2, fontWeight: 700 }}>
        {t('sharedFilesConfiguration')}
      </Typography>
      <Typography variant="body2" sx={{ color: colors.grey[400], mb: 3 }}>
        {t('sharedFilesDescription')}
      </Typography>

      {/* Selected Files Section */}
      {sharedFiles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: colors.greenAccent[400], mb: 2, fontWeight: 600 }}>
            {t('selectedFiles')} ({sharedFiles.length})
          </Typography>
          
          {sharedFiles.map((sharedFile) => {
            const file = getFileById(sharedFile.fileId);
            return (
              <Accordion
                key={sharedFile.fileId}
                expanded={expandedFile === sharedFile.fileId}
                onChange={(e, isExpanded) => setExpandedFile(isExpanded ? sharedFile.fileId : null)}
                sx={{
                  mb: 2,
                  backgroundColor: colors.primary[400],
                  borderRadius: "12px !important",
                  border: `1px solid ${colors.greenAccent[500]}40`,
                  "&:before": { display: "none" },
                  boxShadow: `0 2px 8px ${colors.greenAccent[500]}20`,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: colors.grey[300] }} />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                    <FolderIcon sx={{ color: colors.greenAccent[400], fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                        {file?.name || sharedFile.fileName || t('unknownFile')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                        {t('fileId')} {sharedFile.fileId.substring(0, 8)}...
                      </Typography>
                    </Box>
                  </Box>
                  
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(sharedFile.fileId);
                    }}
                    sx={{
                      color: colors.redAccent[400],
                      "&:hover": {
                        backgroundColor: `${colors.redAccent[700]}40`,
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </AccordionSummary>

                <AccordionDetails>
                  <Box sx={{ pt: 2 }}>
                    {/* File Level Permissions */}
                    <Paper
                      sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: `${colors.primary[500]}`,
                        borderRadius: "8px",
                        border: `1px solid ${colors.grey[700]}40`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <FolderIcon sx={{ color: colors.blueAccent[400], fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                          {t('fileLevelPermissions')}
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.can_view}
                                onChange={(e) => handleFilePermissionChange(sharedFile.fileId, "can_view", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('view')}</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.can_update}
                                onChange={(e) => handleFilePermissionChange(sharedFile.fileId, "can_update", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('update')}</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.can_delete}
                                onChange={(e) => handleFilePermissionChange(sharedFile.fileId, "can_delete", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('delete')}</Typography>}
                          />
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Forms Permissions */}
                    <Paper
                      sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: `${colors.primary[500]}`,
                        borderRadius: "8px",
                        border: `1px solid ${colors.grey[700]}40`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <ArticleIcon sx={{ color: colors.greenAccent[400], fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                          {t('formsPermissions')}
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4} md={2}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.forms.can_view}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "forms", "can_view", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('view')}</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.forms.can_update}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "forms", "can_update", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('update')}</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.forms.can_delete}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "forms", "can_delete", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('delete')}</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.forms.can_add}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "forms", "can_add", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('add')}</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.forms.can_send || false}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "forms", "can_send", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('send')}</Typography>}
                          />
                          <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", ml: 4, mt: -0.5 }}>
                            {t('sendInvitations')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.forms.can_fill || false}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "forms", "can_fill", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('fill')}</Typography>}
                          />
                          <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", ml: 4, mt: -0.5 }}>
                            {t('fillForms')}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Patients Permissions */}
                    <Paper
                      sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: `${colors.primary[500]}`,
                        borderRadius: "8px",
                        border: `1px solid ${colors.grey[700]}40`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <PeopleIcon sx={{ color: colors.blueAccent[400], fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                          {t('patientsPermissions')}
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.patients.can_view}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "patients", "can_view", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('view')}</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.patients.can_update}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "patients", "can_update", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('update')}</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.patients.can_delete}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "patients", "can_delete", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('delete')}</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={sharedFile.patients.can_add}
                                onChange={(e) => handleSubPermissionChange(sharedFile.fileId, "patients", "can_add", e.target.checked)}
                                sx={{
                                  color: colors.grey[600],
                                  '&.Mui-checked': {
                                    color: colors.greenAccent[500],
                                  },
                                }}
                              />
                            }
                            label={<Typography sx={{ color: colors.grey[100] }}>{t('add')}</Typography>}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      <Divider sx={{ my: 3, backgroundColor: `${colors.grey[700]}40` }} />

      {/* Available Files to Add */}
      <Box>
        <Typography variant="h6" sx={{ color: colors.blueAccent[400], mb: 2, fontWeight: 600 }}>
          {t('availableFiles')}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.grey[400], mb: 2 }}>
          {t('clickFileToAdd')}
        </Typography>

        {availableFiles.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.grey[400], textAlign: "center", py: 3 }}>
            {t('noFilesAvailable')}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {availableFiles
              .filter(file => !isFileSelected(file.id))
              .map((file) => (
                <Grid item xs={12} sm={6} md={4} key={file.id}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: colors.primary[500],
                      borderRadius: "10px",
                      border: `1px solid ${colors.grey[700]}40`,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: colors.greenAccent[500],
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
                      },
                    }}
                    onClick={() => handleAddFile(file)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <FolderIcon sx={{ color: colors.blueAccent[400], fontSize: 24 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          {file.id.substring(0, 8)}...
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        sx={{
                          color: colors.greenAccent[400],
                          backgroundColor: `${colors.greenAccent[700]}40`,
                          "&:hover": {
                            backgroundColor: `${colors.greenAccent[700]}60`,
                          },
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}

